import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Map } from 'immutable'

import { Modal, Checkbox } from '@material-ui/core'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'

import { addDeviceToUser } from 'common/actions/system'
import { getUsersRaw } from 'common/selectors/user'

import styles from './AddDeviceContainer.module.scss'

const formatMacInput = raw => {
  const cleaned = raw
    .toUpperCase()
    .replace(/[^0-9A-F]/g, '')
    .slice(0, 12)
  const parts = []
  for (let i = 0; i < cleaned.length; i += 4) {
    parts.push(cleaned.slice(i, i + 4))
  }
  return parts.join('-')
}

const isValidMac = formatted => /^[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}$/.test(formatted)

const AddDeviceContainer = ({ hideModal, users, addDeviceToUser }) => {
  const [step, setStep] = useState(1)
  const [emailQuery, setEmailQuery] = useState('')
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [deviceName, setDeviceName] = useState('')
  const [deviceIdInput, setDeviceIdInput] = useState('')
  const [deviceList, setDeviceList] = useState({})
  const [selectedDevices, setSelectedDevices] = useState([])
  const [selectedProject, setSelectedProject] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [licenseError, setLicenseError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const usersJS = useMemo(() => (users ? users.toArray().map(u => u.toJS()) : []), [users])

  const selectedUser = useMemo(
    () => usersJS.find(u => u._id === selectedUserId),
    [usersJS, selectedUserId]
  )

  const suggestions = useMemo(() => {
    if (!emailQuery) return []
    const q = emailQuery.toLowerCase()
    return usersJS.filter(u => u.email && u.email.toLowerCase().includes(q)).slice(0, 8)
  }, [usersJS, emailQuery])

  const nTotal = Number((selectedUser && selectedUser.nDispositivos) || 0)
  const nLimit = Number((selectedUser && selectedUser.nDispositivosLimit) || 0)

  const handleClose = () => {
    if (isSubmitting) return
    hideModal()
  }

  // Step 1
  const goToStep2 = () => {
    if (!selectedUserId) {
      setFormError('Selecciona un usuario destino')
      return
    }
    setFormError('')
    setStep(2)
  }

  // Step 2
  const handleAddDeviceToList = () => {
    const id = deviceIdInput.toUpperCase()
    setLicenseError('')
    if (!deviceName.trim()) {
      setFormError('Falta el nombre del dispositivo')
      return
    }
    if (!isValidMac(id)) {
      setFormError('El Device ID debe tener formato XXXX-XXXX-XXXX')
      return
    }
    if (deviceList[id]) {
      setFormError('Ese dispositivo ya está en la lista')
      return
    }
    if (selectedUser && selectedUser.isPremium && nLimit > 0) {
      if (nTotal + Object.keys(deviceList).length + 1 > nLimit) {
        setLicenseError(
          `Sin licencias disponibles (${nTotal}/${nLimit}). El usuario debe ampliar su plan.`
        )
        return
      }
    }
    setDeviceList(prev => ({ ...prev, [id]: deviceName.trim() }))
    setDeviceName('')
    setDeviceIdInput('')
    setFormError('')
  }

  const handleRemoveSelected = () => {
    if (selectedDevices.length === 0) return
    setDeviceList(prev => {
      const next = { ...prev }
      selectedDevices.forEach(id => delete next[id])
      return next
    })
    setSelectedDevices([])
    setLicenseError('')
  }

  const toggleDeviceSelection = id => {
    setSelectedDevices(prev => (prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    const all = Object.keys(deviceList)
    if (selectedDevices.length === all.length) setSelectedDevices([])
    else setSelectedDevices(all)
  }

  const goToStep3 = () => {
    if (Object.keys(deviceList).length === 0) {
      setFormError('Añade al menos un dispositivo')
      return
    }
    setFormError('')
    setStep(3)
  }

  // Step 3
  const handleConfirm = () => {
    setIsSubmitting(true)
    setSubmitError('')
    addDeviceToUser({
      customerId: selectedUserId,
      data: {
        deviceList,
        selectedProject: selectedProject || undefined
      },
      onComplete: ({ ok, status, body }) => {
        setIsSubmitting(false)
        if (ok) {
          hideModal()
          return
        }
        if (status === 411) {
          setSubmitError(
            'No quedan licencias disponibles para ese usuario. Amplía su plan e inténtalo de nuevo.'
          )
        } else if (body && body.systemFailed && body.systemFailed.length > 0) {
          setSubmitError(
            `No se pudieron añadir: ${body.systemFailed.map(f => f.id || f).join(', ')}`
          )
        } else {
          setSubmitError('No se pudo añadir el dispositivo. Inténtalo de nuevo.')
        }
        setStep(2)
      }
    })
  }

  // -------------------- Render --------------------
  const renderStepIndicator = current => (
    <div className={styles.steps}>
      <span className={current === 1 ? styles.stepActive : styles.stepInactive}>1. Cuenta</span>
      <span className={current === 2 ? styles.stepActive : styles.stepInactive}>
        2. Dispositivos
      </span>
    </div>
  )

  const renderStep1 = () => (
    <>
      <h2 className={styles.title}>Añadir dispositivos</h2>
      {renderStepIndicator(1)}

      <p className={styles.sectionLabel}>Cuenta destino</p>

      <input
        className={styles.input}
        type='text'
        placeholder='Buscar usuario por email'
        value={emailQuery}
        onChange={e => setEmailQuery(e.target.value)}
      />

      {emailQuery && suggestions.length > 0 && (
        <div className={styles.userList}>
          {suggestions.map(u => (
            <div
              key={u._id}
              className={styles.userSuggestion}
              onClick={() => {
                setSelectedUserId(u._id)
                setEmailQuery('')
                setFormError('')
              }}
            >
              <input
                type='radio'
                checked={selectedUserId === u._id}
                onChange={() => setSelectedUserId(u._id)}
              />
              {u.email}
            </div>
          ))}
        </div>
      )}

      {selectedUser && (
        <>
          <div className={styles.userSuggestion}>
            <input
              type='radio'
              checked
              readOnly
            />
            {selectedUser.email}
          </div>
          {selectedUser.isPremium && (
            <p className={styles.licenseInfo}>
              Licencias: {nTotal}/{nLimit || '∞'}
            </p>
          )}
        </>
      )}

      {formError && (
        <div className={styles.errorBanner}>
          <ErrorOutlineIcon className={styles.errorBannerIcon} />
          <span>{formError}</span>
        </div>
      )}

      <button
        type='button'
        className={styles.buttonPrimary}
        onClick={goToStep2}
        disabled={!selectedUserId}
      >
        SIGUIENTE
        <ArrowForwardIcon className={styles.arrowIcon} />
      </button>
      <button
        type='button'
        className={styles.buttonTextCancel}
        onClick={handleClose}
      >
        CANCELAR
      </button>
    </>
  )

  const renderStep2 = () => {
    const allDeviceIds = Object.keys(deviceList)
    const allChecked = allDeviceIds.length > 0 && selectedDevices.length === allDeviceIds.length
    return (
      <>
        <h2 className={styles.title}>Añadir dispositivos</h2>
        {renderStepIndicator(2)}

        <p className={styles.sectionLabel}>Nuevo dispositivo</p>

        <input
          className={styles.input}
          type='text'
          placeholder='Nombre del dispositivo'
          maxLength={36}
          value={deviceName}
          onChange={e => setDeviceName(e.target.value)}
        />

        <div className={styles.inputWithIcon}>
          <input
            className={styles.input}
            type='text'
            placeholder='Device ID'
            maxLength={14}
            value={deviceIdInput}
            onChange={e => setDeviceIdInput(formatMacInput(e.target.value))}
          />
          <InfoOutlinedIcon className={styles.infoIcon} />
        </div>

        <button
          type='button'
          className={styles.buttonOutline}
          onClick={handleAddDeviceToList}
        >
          ADD DEVICE
        </button>

        {(formError || licenseError || submitError) && (
          <div className={styles.errorBanner}>
            <ErrorOutlineIcon className={styles.errorBannerIcon} />
            <span>{licenseError || submitError || formError}</span>
          </div>
        )}

        {allDeviceIds.length > 0 && (
          <>
            <div className={styles.devicesToAddHeader}>
              <Checkbox
                checked={allChecked}
                onChange={toggleSelectAll}
                size='small'
              />
              <span className={styles.devicesToAddLabel}>Dispositivos a añadir</span>
            </div>
            <div className={styles.deviceList}>
              {allDeviceIds.map(id => (
                <div
                  key={id}
                  className={styles.deviceListItem}
                >
                  <Checkbox
                    checked={selectedDevices.includes(id)}
                    onChange={() => toggleDeviceSelection(id)}
                    size='small'
                    style={{ color: '#FF3152' }}
                  />
                  <span>
                    {deviceList[id]} <small>({id})</small>
                  </span>
                </div>
              ))}
            </div>

            <button
              type='button'
              className={styles.buttonDanger}
              onClick={handleRemoveSelected}
              disabled={selectedDevices.length === 0}
            >
              DELETE FROM LIST
            </button>
          </>
        )}

        <p className={styles.sectionLabel}>Asignar dispositivos a un proyecto</p>
        <select
          className={`${styles.input} ${styles.select}`}
          value={selectedProject}
          onChange={e => setSelectedProject(e.target.value)}
        >
          <option value=''>Seleccionar un proyecto (opcional)</option>
        </select>

        <button
          type='button'
          className={styles.buttonOutline}
          onClick={() => {
            setSubmitError('')
            setStep(1)
          }}
        >
          REGRESAR
        </button>
        <button
          type='button'
          className={styles.buttonPrimary}
          onClick={goToStep3}
          disabled={allDeviceIds.length === 0}
        >
          AÑADIR
          <ArrowForwardIcon className={styles.arrowIcon} />
        </button>
        <button
          type='button'
          className={styles.buttonTextCancel}
          onClick={handleClose}
        >
          CANCELAR
        </button>
      </>
    )
  }

  const renderStep3 = () => {
    const entries = Object.entries(deviceList)
    const isSingle = entries.length === 1
    return (
      <>
        <h3 className={styles.confirmTitle}>Confirmación</h3>
        {isSingle ? (
          <p className={styles.confirmText}>
            Vas a añadir el dispositivo{' '}
            <span className={styles.confirmDeviceName}>{entries[0][1]}</span>{' '}
            <span className={styles.confirmDeviceId}>(id: {entries[0][0]})</span> a la cuenta{' '}
            <span className={styles.confirmDeviceName}>{selectedUser && selectedUser.email}</span>
          </p>
        ) : (
          <div className={styles.confirmText}>
            Vas a añadir {entries.length} dispositivos a la cuenta{' '}
            <span className={styles.confirmDeviceName}>{selectedUser && selectedUser.email}</span>:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {entries.map(([id, name]) => (
                <li key={id}>
                  <span className={styles.confirmDeviceName}>{name}</span>{' '}
                  <span className={styles.confirmDeviceId}>(id: {id})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.confirmButtons}>
          <button
            type='button'
            className={styles.confirmCancel}
            onClick={() => setStep(2)}
            disabled={isSubmitting}
          >
            CANCELAR
          </button>
          <button
            type='button'
            className={styles.confirmAccept}
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Añadiendo...' : 'CONFIRMAR'}
          </button>
        </div>
      </>
    )
  }

  return (
    <Modal
      open
      onClose={handleClose}
      className={styles.modalRoot}
      BackdropProps={{ classes: { root: styles.backdrop } }}
    >
      <div className={step === 3 ? styles.smallCard : styles.modalCard}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </Modal>
  )
}

AddDeviceContainer.propTypes = {
  hideModal: PropTypes.func.isRequired,
  users: PropTypes.instanceOf(Map),
  addDeviceToUser: PropTypes.func.isRequired
}

const mapStateToProps = state => ({
  users: getUsersRaw(state)
})

const mapActionsToProps = {
  addDeviceToUser
}

export default connect(mapStateToProps, mapActionsToProps)(AddDeviceContainer)
